import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Portal from '../Portal';
import { useTracking } from '../../hooks/useTracking';

interface InsufficientCreditsModalProps {
  current_credits: number;
  required_credits: number;
  message: string;
  onClose: () => void;
}

export function InsufficientCreditsModal({
  current_credits,
  required_credits,
  message,
  onClose,
}: InsufficientCreditsModalProps) {
  const navigate = useNavigate();
  const { track } = useTracking();

  // Lock body scroll when modal is open and track modal display
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Track modal display event
    const sourcePage = window.location.pathname;
    const creditsDeficit = required_credits - current_credits;
    
    track('insufficient_credits_modal_shown', {
      source_page: sourcePage,
      current_credits: current_credits,
      required_credits: required_credits,
      credits_deficit: creditsDeficit,
      timestamp: Date.now(),
    });
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [track, current_credits, required_credits]);

  const handleUpgrade = () => {
    // Track upgrade button click before navigation
    const sourcePage = window.location.pathname;
    const creditsDeficit = required_credits - current_credits;
    
    track('insufficient_credits_upgrade_clicked', {
      source_page: sourcePage,
      current_credits: current_credits,
      required_credits: required_credits,
      credits_deficit: creditsDeficit,
      timestamp: Date.now(),
    });
    
    onClose();
    navigate('/upgrade');
  };

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <div className="bg-bg border border-white/20 rounded-xl p-6 sm:p-8 w-full max-w-md shadow-lg">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white text-center">Insufficient Credits</h2>
          </div>

          <div className="mb-8">
            <p className="text-white/90 text-lg text-center">
              You don't have enough credits to generate a video.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4 mb-6 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-white/70">Current credits:</span>
              <span className="text-white font-semibold">{current_credits}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/70">Required:</span>
              <span className="text-white font-semibold">{required_credits}</span>
            </div>
          </div>

          <button
            onClick={handleUpgrade}
            className="w-full bg-[#F9F7EA] text-[#0f1218] px-6 py-3 min-h-[48px] rounded-full text-base font-semibold hover:bg-[#E7D7B1] transition-colors"
          >
            Upgrade
          </button>
        </div>
      </div>
    </Portal>
  );
}

